import React from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface Row {
  link_type: 'open' | 'closed';
  zone_name: string;
  window_name: string;
  offset_type: 'before' | 'after' | 'on';
  offset_days: number;
}

interface Props {
  rows: Row[];
  onChangeRow: (i: number, patch: Partial<Row>) => void;
  onAddRow: () => void;
  onDeleteRow: (i: number) => void;
  zones: any[];
  schedulingWindows: any[];
  forbiddenLinkWindowIds?: Set<number>;
}

const LinksTable: React.FC<Props> = ({ rows, onChangeRow, onAddRow, onDeleteRow, zones, schedulingWindows, forbiddenLinkWindowIds }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <Button onClick={onAddRow}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          {t('Add Link')}
        </Button>
      </div>

      <Table bordered responsive size="sm">
        <thead>
          <tr>
            <th>{t('Type')}</th>
            <th>{t('Zone')}</th>
            <th>{t('Window')}</th>
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const zoneObj = zones.find((z) => String(z.zone_id) === String(r.zone_name) || z.name === r.zone_name);
            const zoneName = zoneObj?.name ?? '';
            const filteredWindows = schedulingWindows.filter(
              (w: any) => (!zoneName || w.zone_name === zoneName) && !forbiddenLinkWindowIds?.has(w.scheduling_window_id)
            );

            return (
              <tr key={i}>
                <td>
                  <Form.Select
                    value={r.link_type}
                    onChange={(e) =>
                      onChangeRow(i, {
                        link_type: e.target.value as Row['link_type'],
                      })
                    }
                  >
                    <option value="open">{t('Open')}</option>
                    <option value="closed">{t('Closed')}</option>
                  </Form.Select>
                </td>
                <td>
                  <Form.Select value={r.zone_name} onChange={(e) => onChangeRow(i, { zone_name: e.target.value })}>
                    <option value="">{t('Any Zone')}</option>
                    {zones.map((z) => (
                      <option key={z.zone_id} value={z.zone_id}>
                        {z.name}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>
                  <Form.Select value={r.window_name} onChange={(e) => onChangeRow(i, { window_name: e.target.value })}>
                    <option value="">{t('Select Window')}</option>
                    {filteredWindows.map((w: any) => (
                      <option key={w.scheduling_window_id} value={w.name}>
                        {w.name}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td className="text-center">
                  <Button variant="danger" size="sm" onClick={() => onDeleteRow(i)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="text-muted text-center">
                {t('No links yet')}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
};

export default LinksTable;
